import React, { PureComponent } from 'react';
import styled from '../theme';
import Tooltip from 'rc-tooltip';
import InfoIcon from '../assets/icons/InfoGrey.svg';
import {
  importQBOClients,
  fetchQBOCustomers,
  quickbooksCheckIsConnected,
  quickbooksOauth,
  revoke,
} from '../api/quickbooks';
import quickbookPng from '../assets/images/quickbook.png';
import { RouteComponentProps } from 'react-router-dom';
import { Bind } from 'lodash-decorators';
import { ClientsImportModal } from '../modals/clients_import_modal';
import { CantConnectModal } from '../modals/cant_connect_modal';
import { FadeLoader } from 'react-spinners';
import { trackView } from '../components/analytics/track_view';
import { MixpanelEvents } from '../components/analytics/mix_panel_events';
import { checkGate } from '../statsig';
import SuccessModal from '../modals/success-modal';
import { Button } from '../components/ui/button';
import Checkbox from '../ui_components/checkbox/checkbox';
import { Modal, ModalActions, ModalContent, ModalHeader } from '../ui_components/modal';
import Select from '../ui_components/select/select';

const DuplicateSpinner = styled.div`
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  z-index: 9999 !important;
`;

const QuickBookConnectButton = styled(Button)`
  box-shadow: none !important;
  border: 1px solid #c6c6c6 !important;
  border-radius: 0.5em !important;
  background-color: #2c9f1c !important;
  display: block !important;
  color: #ffffff !important;
  height: 36px !important;
  font-size: 14px !important;
  line-height: 16px !important;
  &:hover,
  &.active {
    background-color: #2c9f1c !important;
  }
  &:focus {
    border: 1px solid #2c9f1c !important;
  }
`;

const QuickBookDisconnectButton = styled(Button)`
  box-shadow: none;
  border: 1px solid #c6c6c6;
  border-radius: 0.5em;
  background-color: #f6f6f6;
  display: block;
  color: #222222;
  height: 36px;
  font-size: 14px;
  line-height: 16px;
  &:hover,
  &.active {
    background-color: #f6f6f6;
  }
  &:focus {
    border: 1px solid #2c9f1c;
  }
`;

const Text = styled.span`
  font-family: Poppins !important;
  //margin-bottom: 20px !important;
`;
const Image = styled.img`
  margin-bottom: 20px !important;
  font-size: 18px !important;
`;

type BillingTypeOption = 'Retail' | 'Trade';
type BillToOption = 'Self' | 'Parent';

interface QuickbooksCustomer {
  Id?: string | number;
  DisplayName?: string;
  ParentRef?: { value?: string; name?: string };
  Job?: boolean;
  Level?: number;
  Taxable?: boolean;
  BillWithParent?: boolean | null;
}

const BILLING_TYPE_OPTIONS: BillingTypeOption[] = ['Trade', 'Retail'];
const BILL_TO_OPTIONS: BillToOption[] = ['Self', 'Parent'];

const ReviewModal = styled(Modal)`
  max-width: 960px !important;
  width: 100% !important;
  border-radius: 16px !important;
  border: 1px solid #e3ece7 !important;
`;

const ReviewModalHeader = styled(ModalHeader)`
  border-bottom: none !important;
  font-size: 20px !important;
  font-weight: 600 !important;
  padding-bottom: 0 !important;
  color: #1f3c2e !important;
`;

const ReviewModalContent = styled(ModalContent)`
  display: flex !important;
  flex-direction: column !important;
  gap: 20px !important;
  padding-top: 0 !important;
  height: 70vh !important;
`;

const ReviewSummaryHeading = styled.div`
  font-size: 20px !important;
  font-weight: 600 !important;
  color: #1f3c2e !important;
`;

const ReviewTable = styled.div`
  border: 1px solid #ddebe2 !important;
  border-radius: 12px !important;
  overflow: hidden !important;
  height: 600px !important;
  overflow-y: auto !important;
`;

const ReviewTableHeader = styled.div`
  display: grid !important;
  /* allow shrinking with minmax(0, …) */
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr) minmax(0, 2fr) minmax(0, 1.5fr) !important;
  background: #ffffff !important;
  color: #1f3c2e !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  padding: 12px 20px !important;
`;

const ReviewTableRow = styled.div<{ $isSelected?: boolean; $isDisabled?: boolean }>`
  /* NEW: $isDisabled for subtle dim */
  display: grid !important;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr) minmax(0, 2fr) minmax(0, 1.5fr) !important;
  align-items: center !important;
  padding: 12px 20px !important;
  background: ${(p) => (p.$isSelected ? '#f9fffc' : '#ffffff')} !important;
  border-top: 1px solid #f0f4f2 !important;
  opacity: ${(p) => (p.$isDisabled ? 0.6 : 1)} !important; /* NEW: visual cue for disabled rows */
`;

/* base cell */
const ReviewTableCell = styled.div`
  font-size: 14px !important;
  color: #222 !important;
  min-height: 32px !important;
  display: flex !important;
  align-items: center !important;
  min-width: 0 !important; /* <-- crucial for grid shrink */
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  margin-left: 3px !important;
`;

/* text-only cells need truncation */
const TextCell = styled(ReviewTableCell)`
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
`;

const ClientCell = styled(ReviewTableCell)`
  gap: 8px !important;
  min-width: 0 !important; /* let checkbox+label shrink */
  .label {
    min-width: 0 !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    display: inline-block !important; /* ensure ellipsis works */
  }
`;

const SelectionCheckbox = styled(Checkbox)`
  .label {
    padding-left: 8px !important;
    font-size: 14px !important;
  }
`;

const ReviewSelect = styled(Select)`
  width: 100% !important;
  .select {
    width: 100% !important;
    border: 1px solid #d6e6db !important;
    border-radius: 6px !important;
    padding: 6px 32px 6px 12px !important;
    font-size: 14px !important;
    color: #1f3c2e !important;
    background-color: #ffffff !important;
  }
`;

const ReviewModalActions = styled(ModalActions)`
  justify-content: space-between !important;
  padding: 16px 24px !important;
  margin-left: auto !important;
  border-top: none !important;
`;

const ModalActionsGroup = styled.div`
  display: flex !important;
  gap: 12px !important;
`;

const CancelImportButton = styled(Button)`
  background-color: transparent !important;
  color: #1f3c2e !important;
  border: 1px solid #ccd9d1;
  border-radius: 8px;
  padding: 10px 18px;
  box-shadow: none;
  &:hover {
    background-color: #f0f4f2 !important;
  }
`;

const ImportButton = styled(Button)`
  background-color: #3b97b1 !important;
  color: #ffffff !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 20px !important;
  box-shadow: none !important;
  &:hover,
  &:focus {
    background-color: #31839a !important;
  }
  &:disabled,
  &[uiDisabled='true'] {
    background-color: #bcd3da !important;
    cursor: not-allowed !important;
  }
`;

const BillingHeader = styled.div`
  display: flex !important;
  align-items: center !important;
  gap: 6px !important;
`;

const TooltipText = styled.div`
  max-width: 280px !important;
  font-size: 12px !important;
  line-height: 1.4 !important;
  color: #ffffff !important;
`;

const InfoIconWrapper = styled.span`
  display: inline-flex !important;
  width: 18px !important;
  height: 18px !important;
  svg {
    width: 18px !important;
    height: 18px !important;
  }
`;

interface QuickBookConnectProps extends RouteComponentProps {
  accountId: number;
  isConnected: boolean;
  setConnected: (v: boolean) => void;
}

interface StateProps {
  spinner: boolean;
  // accessToken: string | null;
  syncClients: boolean;
  exportClientsToQB: boolean;
  importClientsFromQB: boolean;
  isImportModalVisible: boolean;
  isCantConnectModalOpen: boolean;
  showImportCheckbox?: boolean; // <- track gate result
  customers: QuickbooksCustomer[];
  selectedCustomerIds: Set<string>;
  billingTypeSelections: Record<string, BillingTypeOption>;
  billToSelections: Record<string, BillToOption>;
  isClientReviewModalVisible: boolean;
  customersLoading: boolean;
  customerFetchError?: string | null;

  // NEW: parent/children maps for hierarchy enforcement
  parentById: Record<string, string | null>;
  childrenById: Record<string, string[]>;
  showModal: boolean;
}

class QuickBookConnect extends PureComponent<QuickBookConnectProps, StateProps> {
  private hasTrackedSuccess = false;
  private selectAllCheckboxRef = React.createRef<HTMLInputElement>();

  constructor(props: QuickBookConnectProps) {
    super(props);
  }

  public state: StateProps = {
    spinner: false,
    isCantConnectModalOpen: false,
    // accessToken: null,
    syncClients: true,
    exportClientsToQB: true,
    importClientsFromQB: true,
    isImportModalVisible: false,
    showImportCheckbox: undefined,
    customers: [],
    selectedCustomerIds: new Set<string>(),
    billingTypeSelections: {},
    billToSelections: {},
    isClientReviewModalVisible: false,
    customersLoading: false,
    customerFetchError: null,

    // NEW: init maps
    parentById: {},
    childrenById: {},
    showModal: false,
  };

  private getCustomerId(customer: QuickbooksCustomer): string | null {
    if (customer && customer.Id != null) {
      return String(customer.Id);
    }
    return null;
  }

  private deriveBillingType(customer: QuickbooksCustomer): BillingTypeOption {
    return customer && customer.Taxable ? 'Retail' : 'Trade';
  }

  private deriveBillTo(customer: QuickbooksCustomer): BillToOption {
    return customer && customer.BillWithParent ? 'Parent' : 'Self';
  }

  private stripImportQueryParam() {
    if (
      typeof window === 'undefined' ||
      !window.history ||
      typeof window.history.replaceState !== 'function'
    ) {
      return;
    }
    const url = new URL(window.location.href);
    if (!url.searchParams.get('importClients')) {
      return;
    }
    url.searchParams.delete('importClients');
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, document.title, next);
  }

  private updateSelectAllCheckboxState() {
    const ref = this.selectAllCheckboxRef.current;
    if (!ref) {
      return;
    }
    const total = this.state.customers.length;
    const selected = this.state.selectedCustomerIds.size;
    ref.indeterminate = selected > 0 && selected < total;
  }

  private getSelectedCount(): number {
    return this.state.selectedCustomerIds.size;
  }

  private getParentLabel(
    customer: QuickbooksCustomer,
    customersById: Map<string, QuickbooksCustomer>
  ): string {
    const parentId = customer.ParentRef && customer.ParentRef.value;
    if (!parentId) {
      return 'N/A';
    }
    const parent = customersById.get(String(parentId));
    return (
      (parent && parent.DisplayName) || (customer.ParentRef && customer.ParentRef.name) || 'N/A'
    );
  }

  // NEW: build parent/children maps from flat list
  private buildHierarchy(customers: QuickbooksCustomer[]) {
    const parentById: Record<string, string | null> = {};
    const childrenById: Record<string, string[]> = {};

    customers.forEach((c) => {
      const id = this.getCustomerId(c);
      if (!id) return;
      const p = c.ParentRef && c.ParentRef.value ? String(c.ParentRef.value) : null;
      parentById[id] = p;
      if (p) {
        if (!childrenById[p]) childrenById[p] = [];
        childrenById[p].push(id);
      }
      if (!childrenById[id]) childrenById[id] = []; // ensure key exists
    });

    return { parentById, childrenById };
  }

  // NEW: collect ALL descendants (recursive)
  private getDescendants(id: string): string[] {
    const out: string[] = [];
    const stack = [...(this.state.childrenById[id] || [])];
    while (stack.length) {
      const cur = stack.pop()!;
      out.push(cur);
      const kids = this.state.childrenById[cur] || [];
      for (const k of kids) stack.push(k);
    }
    return out;
  }

  // NEW: check if ALL ancestors are selected
  private hasAllAncestorsSelected(
    id: string,
    selected: Set<string> = this.state.selectedCustomerIds
  ): boolean {
    let parent = this.state.parentById[id] || null;
    while (parent) {
      if (!selected.has(parent)) return false;
      parent = this.state.parentById[parent] || null;
    }
    return true;
  }

  private hydrateCustomers(customers: QuickbooksCustomer[]) {
    const ids: string[] = [];
    const billingTypeSelections: Record<string, BillingTypeOption> = {};
    const billToSelections: Record<string, BillToOption> = {};

    customers.forEach((customer) => {
      const id = this.getCustomerId(customer);
      if (!id) {
        return;
      }
      ids.push(id);
      billingTypeSelections[id] = this.deriveBillingType(customer);
      billToSelections[id] = this.deriveBillTo(customer);
    });

    const { parentById, childrenById } = this.buildHierarchy(customers); // NEW

    this.setState(
      {
        customers,
        selectedCustomerIds: new Set<string>(ids),
        billingTypeSelections,
        billToSelections,
        isClientReviewModalVisible: customers.length > 0,
        customersLoading: false,
        customerFetchError: null,
        parentById, // NEW
        childrenById, // NEW
        showModal: customers.length === 0,
      },
      () => {
        this.updateSelectAllCheckboxState();
        this.stripImportQueryParam();
      }
    );
  }

  private handleCustomersFetchError(message: string) {
    this.setState(
      {
        customersLoading: false,
        customerFetchError: message,
        isClientReviewModalVisible: true,
        showModal: false,
        parentById: {}, // NEW: reset maps
        childrenById: {}, // NEW: reset maps
      },
      () => {
        this.updateSelectAllCheckboxState();
        this.stripImportQueryParam();
      }
    );
  }

  @Bind()
  private handleSelectAllToggle() {
    this.setState((prevState) => {
      const allIds = prevState.customers
        .map((customer) => this.getCustomerId(customer))
        .filter((id): id is string => Boolean(id));
      if (prevState.selectedCustomerIds.size === allIds.length) {
        return { selectedCustomerIds: new Set<string>() };
      }
      // Selecting all selects parents and children together; rule is satisfied.
      return { selectedCustomerIds: new Set<string>(allIds) };
    }, this.updateSelectAllCheckboxState);
  }

  @Bind()
  private handleCustomerToggle(customerId: string) {
    this.setState((prevState) => {
      const next = new Set(prevState.selectedCustomerIds);
      const isSelected = next.has(customerId);

      if (!isSelected) {
        // Trying to select; only allow if all ancestors are selected
        if (!this.hasAllAncestorsSelected(customerId, next)) {
          // Block selection when parent chain not selected
          return { selectedCustomerIds: next };
        }
        next.add(customerId);
      } else {
        // Deselect this id AND all its descendants
        next.delete(customerId);
        const descendants = this.getDescendants(customerId);
        for (const id of descendants) next.delete(id);
      }

      return { selectedCustomerIds: next };
    }, this.updateSelectAllCheckboxState);
  }

  @Bind()
  private handleBillingTypeChange(customerId: string, value: BillingTypeOption) {
    this.setState((prevState) => ({
      billingTypeSelections: { ...prevState.billingTypeSelections, [customerId]: value },
    }));
  }

  @Bind()
  private handleBillToChange(customerId: string, value: BillToOption) {
    this.setState((prevState) => ({
      billToSelections: { ...prevState.billToSelections, [customerId]: value },
    }));
  }

  @Bind()
  private async handleImportSelected() {
    const { accountId } = this.props;
    const { selectedCustomerIds, customers, billingTypeSelections, billToSelections } = this.state;

    const selectedIds = Array.from(selectedCustomerIds);
    if (selectedIds.length === 0) return;

    // index customers for quick lookup
    const byId = new Map<string, QuickbooksCustomer>();
    for (const c of customers) {
      const id = this.getCustomerId(c);
      if (id) byId.set(id, c);
    }

    const toBoolTaxable = (billing: BillingTypeOption | undefined) =>
      (billing || 'Retail') === 'Retail';
    const toBoolBillWithParent = (billTo: BillToOption | undefined) =>
      (billTo || 'Self') === 'Parent';

    type Mapped = {
      qbId: string;
      displayName?: string;
      parentQbId: string | null;
      job: boolean;
      taxable: boolean;
      billWithParent: boolean;
      billingType: BillingTypeOption;
      billToParent: 0 | 1;
    };

    // Build the mapped payload once
    const mappedAll: Mapped[] = selectedIds
      .map((id) => {
        const qb = byId.get(id);
        if (!qb) return null;

        const billing = billingTypeSelections[id] || this.deriveBillingType(qb);
        const billTo = billToSelections[id] || this.deriveBillTo(qb);

        return {
          qbId: id,
          displayName: qb.DisplayName,
          parentQbId: (qb && qb.ParentRef && qb.ParentRef.value) || null,
          job: Boolean(qb.Job),
          taxable: toBoolTaxable(billing),
          billWithParent: toBoolBillWithParent(billTo),
          billingType: billing,
          billToParent: billTo === 'Self' ? 0 : 1,
        };
      })
      .filter(Boolean) as Mapped[];

    // --- batching helpers ---
    const chunk = <T,>(arr: T[], size: number) => {
      const out: T[][] = [];
      for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
      return out;
    };

    // simple concurrency limiter (no deps)
    const runPool = async <T,>(tasks: (() => Promise<T>)[], concurrency: number) => {
      const results: T[] = [];
      let i = 0;
      const workers = Array(Math.min(concurrency, tasks.length))
        .fill(0)
        .map(async () => {
          while (i < tasks.length) {
            const cur = i++;
            results[cur] = await tasks[cur]();
          }
        });
      await Promise.all(workers);
      return results;
    };

    // backoff fetch wrapper
    const callImport = async (body: any, maxRetries = 3) => {
      let attempt = 0;
      // If your importQBOClients is a wrapper around fetch/axios, use it; otherwise:
      // return await importQBOClients(accountId, body.customersData, body.mappedCustomers);
      while (true) {
        try {
          return await importQBOClients(accountId, body.customersData, body.mappedCustomers);
        } catch (err) {
          attempt++;
          const status = (err && err.response && err.response.status) || (err && err.status) || 0;

          const retryable = status === 0 || status === 429 || (status >= 500 && status < 600);
          if (!retryable || attempt > maxRetries) throw err;
          const delay = Math.min(2000 * 2 ** (attempt - 1), 8000) + Math.random() * 300;
          await new Promise((r) => setTimeout(r, delay));
        }
      }
    };

    // build batches of mapped selections
    const BATCH_SIZE = 100;
    const batches = chunk(mappedAll, BATCH_SIZE);

    // progress
    let insertedTotal = 0,
      skippedTotal = 0,
      updatedTotal = 0;

    this.setState({ spinner: true });

    try {
      trackView(MixpanelEvents.QB_CONNECT, {
        action: 'confirm_import_review',
        accountId,
        selectedClients: mappedAll.length,
        batches: batches.length,
        batchSize: BATCH_SIZE,
      });

      const tasks = batches.map((mappedBatch, idx) => async () => {
        // send only the needed customers for this batch
        const qbIds = new Set(mappedBatch.map((m) => m.qbId));
        const customersSubset = [...qbIds]
          .map((id) => byId.get(id))
          .filter(Boolean) as QuickbooksCustomer[];

        const result = await callImport({
          customersData: customersSubset,
          mappedCustomers: mappedBatch,
        });

        insertedTotal += (result && result.customerData && result.customerData.inserted) || 0;
        updatedTotal += (result && result.customerData && result.customerData.updated) || 0;
        skippedTotal += (result && result.customerData && result.customerData.skipped) || 0;

        trackView(MixpanelEvents.QB_CONNECT, {
          action: 'import_batch_done',
          accountId,
          batchIndex: idx,
          batchSize: mappedBatch.length,
          inserted: (result && result.customerData && result.customerData.inserted) || 0,
          updated: (result && result.customerData && result.customerData.updated) || 0,
          skipped: (result && result.customerData && result.customerData.skipped) || 0,
        });

        return result;
      });

      // Run with limited parallelism
      const MAX_CONCURRENCY = 3;
      await runPool(tasks, MAX_CONCURRENCY);

      this.setState({
        isClientReviewModalVisible: false,
        spinner: false,
      });

      trackView(MixpanelEvents.QB_CONNECT, {
        action: 'import_all_done',
        accountId,
        clients_number: insertedTotal,
        updatedTotal,
        skippedTotal,
        batches: batches.length,
      });
    } catch (err) {
      console.error('Import clients failed', err);
      this.setState({ spinner: false });
      trackView(MixpanelEvents.QB_CONNECT, {
        action: 'import_clients_failed',
        accountId,
        error: String((err && err.message) || err),
      });
    }
  }

  private renderClientsReviewModal(): React.ReactNode {
    const {
      isClientReviewModalVisible,
      customers,
      customersLoading,
      customerFetchError,
      billingTypeSelections,
      billToSelections,
    } = this.state;

    if (!isClientReviewModalVisible) {
      return null;
    }

    const total = customers.length;
    const selectedCount = this.getSelectedCount();
    const clientColumnTitle = selectedCount > 0 ? `${selectedCount} selected` : '0 selected';

    const customersById = new Map<string, QuickbooksCustomer>();
    customers.forEach((customer) => {
      const id = this.getCustomerId(customer);
      if (id) {
        customersById.set(id, customer);
      }
    });

    return (
      <ReviewModal uiActive={isClientReviewModalVisible} uiOnRequestClose={() => this.setState({})}>
        <ReviewModalHeader>{`${total} Client${total === 1 ? '' : 's'
          } are ready to be imported`}</ReviewModalHeader>
        <ReviewModalContent>
          <ReviewSummaryHeading>
            <br />{' '}
          </ReviewSummaryHeading>
          {customersLoading ? (
            <div>Loading clients from QuickBooks...</div>
          ) : customerFetchError ? (
            <div>{customerFetchError}</div>
          ) : (
            <ReviewTable>
              <ReviewTableHeader>
                <div className="flex items-center gap-2">
                  <input
                    name="markAll"
                    type="checkbox"
                    className={`checkbox ${selectedCount !== total ? 'minus' : ''}`}
                    checked={selectedCount > 0}
                    onChange={this.handleSelectAllToggle}
                  />
                  <span>{clientColumnTitle}</span>
                </div>
                <span>Sub-Client of</span>
                <BillingHeader>
                  <span>Billing Type</span>
                  <Tooltip
                    overlay={
                      <TooltipText>
                        We treat tax exempt clients as Trade and non tax exempt as Retail. Please
                        update as needed for your business.
                      </TooltipText>
                    }
                    placement="top"
                    overlayClassName="qb-billing-tooltip"
                  >
                    <InfoIconWrapper>
                      <InfoIcon />
                    </InfoIconWrapper>
                  </Tooltip>
                </BillingHeader>
                <span>Bill to</span>
              </ReviewTableHeader>

              {customers.map((customer) => {
                const id = this.getCustomerId(customer);
                if (!id) {
                  return null;
                }
                const isSelected = this.state.selectedCustomerIds.has(id);

                // NEW: child can be selected only if all ancestors selected
                const enabledByParent = this.hasAllAncestorsSelected(id);

                const billingValue = billingTypeSelections[id] || this.deriveBillingType(customer);
                const billToValue = billToSelections[id] || this.deriveBillTo(customer);
                return (
                  <ReviewTableRow key={id} $isSelected={isSelected} $isDisabled={!enabledByParent}>
                    <ClientCell>
                      <SelectionCheckbox
                        checked={isSelected}
                        onChange={() => this.handleCustomerToggle(id)}
                        title={
                          enabledByParent
                            ? customer.DisplayName || 'QuickBooks Client'
                            : 'Select the parent first'
                        }
                        disabled={!enabledByParent} /* NEW */
                        uiDisabled={!enabledByParent} /* NEW: for UI lib parity */
                      >
                        <TextCell title={customer.DisplayName}>
                          {customer.DisplayName || 'QuickBooks Client'}
                        </TextCell>
                      </SelectionCheckbox>
                    </ClientCell>

                    <TextCell title={this.getParentLabel(customer, customersById)}>
                      {this.getParentLabel(customer, customersById)}
                    </TextCell>

                    <ReviewTableCell>
                      <ReviewSelect
                        value={billingValue}
                        onChange={(event) =>
                          this.handleBillingTypeChange(id, event.target.value as BillingTypeOption)
                        }
                      >
                        {BILLING_TYPE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </ReviewSelect>
                    </ReviewTableCell>
                    <ReviewTableCell>
                      <ReviewSelect
                        value={billToValue}
                        onChange={(event) =>
                          this.handleBillToChange(id, event.target.value as BillToOption)
                        }
                        disabled={!customer || !customer.ParentRef || !customer.ParentRef.value}
                      >
                        {BILL_TO_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </ReviewSelect>
                    </ReviewTableCell>
                  </ReviewTableRow>
                );
              })}
            </ReviewTable>
          )}
        </ReviewModalContent>
        <ReviewModalActions>
          <ModalActionsGroup>
            <CancelImportButton
              onClick={() => this.setState({ isClientReviewModalVisible: false })}
            >
              Cancel
            </CancelImportButton>
          </ModalActionsGroup>
          <ModalActionsGroup>
            <ImportButton onClick={this.handleImportSelected} uiDisabled={selectedCount === 0}>
              {selectedCount === total && total > 0
                ? 'Import All'
                : `Import ${selectedCount} client${selectedCount === 1 ? '' : 's'}`}
            </ImportButton>
          </ModalActionsGroup>
        </ReviewModalActions>
      </ReviewModal>
    );
  }

  @Bind()
  public async connectToQuickBook() {
    trackView(MixpanelEvents.QB_CONNECT, {
      action: 'click_connect',
      source: 'primary_button',
      accountId: this.props.accountId,
      importClientsFromQB: this.state.importClientsFromQB,
    });

    try {
      if (this.state.importClientsFromQB) {
        this.setState({ isImportModalVisible: true });
        trackView(MixpanelEvents.QB_CONNECT, {
          action: 'show_import_modal',
          accountId: this.props.accountId,
        });
      } else {
        const res = await quickbooksOauth(this.props.accountId, false);

        if (res && res.authUrl) {
          trackView(MixpanelEvents.QB_CONNECT, {
            action: 'redirect_to_intuit',
            accountId: this.props.accountId,
          });
          open(res.authUrl, '_self');
        } else {
          this.setState({ isCantConnectModalOpen: true });
          trackView(MixpanelEvents.QB_CONNECT, {
            action: 'no_auth_url',
            accountId: this.props.accountId,
          });
        }
      }
    } catch (e) {
      console.error('connectToQuickBook error: ', e);
      this.setState({ isCantConnectModalOpen: true });
      trackView(MixpanelEvents.QB_CONNECT, {
        action: 'connect_exception',
        accountId: this.props.accountId,
        error: String(((e as any) && (e as any).message) || e),
      });
    }
  }

  public async componentDidMount() {
    const show = checkGate('import_clients');
    this.setState((prev) => ({
      showImportCheckbox: show,
      importClientsFromQB: show ? prev.importClientsFromQB : false, // hide => force false
    }));

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const errorProp = urlParams.get('error');
      const importParam = urlParams.get('importClients');

      if (importParam === 'true') {
        this.setState({ customersLoading: true, customerFetchError: null, spinner: true });
        try {
          const response = await fetchQBOCustomers(this.props.accountId);
          const customers =
            response && Array.isArray(response.customerData)
              ? (response.customerData as QuickbooksCustomer[])
              : [];
          this.hydrateCustomers(customers);

          this.setState({ customersLoading: false, customerFetchError: null, spinner: false });
        } catch (fetchError) {
          this.setState({ customersLoading: false, spinner: false });
          console.error('Error fetching QuickBooks clients:', fetchError);
          this.handleCustomersFetchError(
            'We could not load your QuickBooks clients. Please try again.'
          );
        }
      }

      if (errorProp) {
        this.setState({ isCantConnectModalOpen: true });
        trackView(MixpanelEvents.QB_CONNECT, {
          action: 'oauth_return_error',
          accountId: this.props.accountId,
          error: errorProp,
        });
      }

      const res = await quickbooksCheckIsConnected(this.props.accountId);
      this.props.setConnected(res.connected);

      if (res.connected && !this.hasTrackedSuccess) {
        this.hasTrackedSuccess = true;
        trackView(MixpanelEvents.QB_CONNECT, {
          action: 'oauth_success',
          accountId: this.props.accountId,
        });
      }
    } catch (e) {
      console.error('Error fetching QuickBooks info:', e);
      this.props.setConnected(false);
      trackView(MixpanelEvents.QB_CONNECT, {
        action: 'status_check_failed',
        accountId: this.props.accountId,
        error: String((e && e.message) || e),
      });
    }
  }

  public async disconnectFromQuickBook() {
    try {
      this.setState({ spinner: true });
      await revoke(this.props.accountId)
        .then((res) => {
          if (res.disconnected) {
            this.props.setConnected(false);
            this.setState({ spinner: false });
            trackView(MixpanelEvents.QB_CONNECT, {
              action: 'disconnect_success',
              accountId: this.props.accountId,
            });
          }
        })
        .catch((err) => {
          this.setState({ spinner: false });
          console.log('error disconnectFromQuickBook');
          trackView(MixpanelEvents.QB_CONNECT, {
            action: 'disconnect_failed',
            accountId: this.props.accountId,
            error: String((err && err.message) || err),
          });
        });
    } catch (e) {
      this.setState({ spinner: false });
      console.error('disconnectFromQuickBook error: ', e);
      trackView(MixpanelEvents.QB_CONNECT, {
        action: 'disconnect_exception',
        accountId: this.props.accountId,
        error: String((e && e.message) || e),
      });
    }
  }

  public async importClientsAndConnect() {
    trackView(MixpanelEvents.QB_CONNECT, {
      action: 'confirm_import_and_connect',
      accountId: this.props.accountId,
    });

    try {
      const res = await quickbooksOauth(this.props.accountId, true);

      if (res && res.authUrl) {
        trackView(MixpanelEvents.QB_CONNECT, {
          action: 'redirect_to_intuit',
          accountId: this.props.accountId,
          withImport: true,
        });
        open(res.authUrl, '_self');
      } else {
        trackView(MixpanelEvents.QB_CONNECT, {
          action: 'no_auth_url',
          accountId: this.props.accountId,
          withImport: true,
        });
      }
    } catch (e) {
      console.error('import clients and connect error: ', e);
      trackView(MixpanelEvents.QB_CONNECT, {
        action: 'connect_exception',
        accountId: this.props.accountId,
        withImport: true,
        error: String((e && e.message) || e),
      });
    }
  }

  componentDidUpdate(prevProps: Readonly<QuickBookConnectProps>, prevState: Readonly<StateProps>) {
    const show = checkGate('qb_connect');
    if (prevState.showImportCheckbox !== show) {
      this.setState((prev) => ({
        showImportCheckbox: show,
        importClientsFromQB: show ? true : false, // only force false when hiding
      }));
    }

    if (
      prevState.selectedCustomerIds !== this.state.selectedCustomerIds ||
      prevState.customers !== this.state.customers
    ) {
      this.updateSelectAllCheckboxState();
    }
  }

  render() {
    const { isConnected } = this.props;
    const { showImportCheckbox, importClientsFromQB } = this.state;
    const reviewModal = this.renderClientsReviewModal();

    return (
      <div>
        {this.state.spinner && (
          <DuplicateSpinner style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}>
            <FadeLoader
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
              }}
              color="#3B97B1"
              aria-label="Loading Spinner"
              data-testid="export-loader"
            />
          </DuplicateSpinner>
        )}
        {reviewModal}
        <SuccessModal
          open={this.state.showModal}
          onClose={() => this.setState({ showModal: false })}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            marginTop: '10%',
          }}
        >
          <Image src={quickbookPng} />
          {isConnected ? (
            <>
              <Text style={{ color: '#008C48' }}>You’re connected to QuickBooks</Text>
              <Text style={{ marginBottom: '20px', color: '#008C48' }}>
                and can export your estimates from QUIPA
              </Text>
            </>
          ) : (
            <>
              <Text style={{ color: '#222222' }}>Link your QuickBooks account to enable</Text>
              <Text style={{ marginBottom: '20px', color: '#222222' }}>
                seamless estimate exports from QUIPA.
              </Text>
              {showImportCheckbox ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    gap: '15px',
                    marginBottom: '20px',
                  }}
                >
                  <Checkbox
                    checked={importClientsFromQB}
                    onChange={() =>
                      this.setState((s) => ({ importClientsFromQB: !s.importClientsFromQB }))
                    }
                  >
                    Import active clients from QuickBooks to QUIPA
                  </Checkbox>
                </div>
              ) : (
                <></>
              )}

              <ClientsImportModal
                uiActive={this.state.isImportModalVisible}
                uiOnConfirm={() => {
                  this.importClientsAndConnect();
                }}
                uiOnRequestClose={() => {
                  this.setState({
                    isImportModalVisible: false,
                  });
                }}
                uiCloseOnOutsideClick={true}
                uiCloseOnEsc={true}
                uiHeader="Client Billing Type"
              >
                <p>
                  When importing clients from QuickBooks, we'll assume that all your tax exempt
                  clients are trade clients.
                </p>

                <div
                  style={{
                    marginBottom: '10px',
                  }}
                >
                  <p style={{ margin: '0 0 0 5px' }}>
                    <strong>• Tax Exempt → Trade</strong> in QUIPA
                  </p>
                  <p style={{ margin: '0 0 0 5px' }}>
                    <strong>• Non-Tax Exempt → Retail</strong> in QUIPA
                  </p>
                </div>

                <p>You can update this later in client list.</p>
              </ClientsImportModal>
            </>
          )}

          {isConnected ? (
            <QuickBookDisconnectButton onClick={() => this.disconnectFromQuickBook()}>
              Disconnect
            </QuickBookDisconnectButton>
          ) : (
            <QuickBookConnectButton onClick={this.connectToQuickBook} name="quick-book">
              Connect to QuickBooks
            </QuickBookConnectButton>
          )}

          <CantConnectModal
            uiActive={this.state.isCantConnectModalOpen}
            uiOnConfirm={() => {
              this.connectToQuickBook();
              this.setState({ isCantConnectModalOpen: false });
            }}
            uiOnRequestClose={() => this.setState({ isCantConnectModalOpen: false })}
            uiCloseOnOutsideClick={true}
            uiCloseOnEsc={true}
            uiHeader="Can't connect to QuickBooks"
          >
            <p>
              {`We are having trouble connecting to your QuickBooks account. Click retry or come back later.`}
            </p>
          </CantConnectModal>
        </div>
      </div>
    );
  }
}

export default QuickBookConnect;
